import { useId } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";

interface UnsavedChangesDialogProps {
  open: boolean;
  // What the prose calls the record, e.g. "control" or "control template".
  entityLabel: string;
  isSaving: boolean;
  // Why the record cannot be saved yet, if it cannot. Disables Save and is
  // shown in the dialog, leaving Cancel and Leave as the ways out.
  saveDisabledReason?: string | null;
  onSave: () => void;
  onLeave: () => void;
  onCancel: () => void;
}

export function UnsavedChangesDialog({
  open,
  entityLabel,
  isSaving,
  saveDisabledReason,
  onSave,
  onLeave,
  onCancel,
}: UnsavedChangesDialogProps) {
  const saveDisabledReasonId = useId();
  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Save your changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes to this {entityLabel}. Do you want to save
            them before leaving? If you leave without saving, your changes will
            be lost.
          </AlertDialogDescription>
          {saveDisabledReason && (
            <p id={saveDisabledReasonId} className="text-sm font-medium">
              {saveDisabledReason}
            </p>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* Cancel uses the Radix slot so it receives default focus and
              Escape/keyboard semantics. Leave and Save stay as plain Buttons:
              AlertDialogAction would auto-close the dialog, but Save must keep
              it open when the underlying save fails validation. */}
          <AlertDialogCancel onClick={onCancel} disabled={isSaving}>
            Cancel
          </AlertDialogCancel>
          <Button variant="outline" onClick={onLeave} disabled={isSaving}>
            Leave without saving
          </Button>
          <Button
            onClick={onSave}
            loading={isSaving}
            disabled={isSaving || !!saveDisabledReason}
            aria-describedby={
              saveDisabledReason ? saveDisabledReasonId : undefined
            }
          >
            Save changes
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
